/**
 * Forgot password: /forgot
 *
 * Step 1 of two. The member types the ONE thing they are sure of — their
 * mobile number or their email — and we look up the address on their record
 * and send a one-time link there.
 *
 * Why a number is accepted at all: most members here know their mobile number
 * by heart and are hazy about which of their two addresses the club has. So
 * they give us the number, and we tell them (masked) which inbox to open.
 * That is the whole design: `la•••••••••ya@gmail.com` is enough to walk to
 * the right app and not enough to be worth harvesting.
 *
 * Both portals use this one screen. A locked-out committee member has exactly
 * the same problem as a locked-out runner, and a second path is a second
 * place to get it wrong.
 *
 * The screen NO LONGER shows the reset link (owner's direction, 29 Jul 2026).
 * The club sends real email; the only copy of a link is in the inbox it was
 * sent to. If the send fails the member is told that, not handed a link — a
 * screen that says "check your email" about a message that was never sent is
 * worse than an error.
 *
 * Re-skinned at the same time: the screen was a bare stack of controls on
 * white, which read as unfinished next to the sign-in page. It now carries the
 * same dark rail, sits on a card, and — the actual point — shows the member
 * where they are in a two-step errand, because half of "I never got the email"
 * is not knowing an email was the next thing to expect.
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatPhone, identify, RESET_TTL_MIN } from '../data';
import { useClub } from '../context/ClubContext';
import { Brand, Feedback, IDENTIFIER_PLACEHOLDER, INPUT, SubmitButton } from './SignIn';

export function ForgotPasswordPage() {
  const { requestPasswordReset } = useClub();
  const [params] = useSearchParams();

  // Prefilled from the sign-in screen, so nobody types their number twice.
  const [identifier, setIdentifier] = useState(params.get('id') ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null); // { maskedEmail }

  const { kind, value: parsed } = identify(identifier);
  const echo = parsed ? (kind === 'phone' ? formatPhone(parsed) : parsed) : null;

  async function submit(e) {
    e?.preventDefault();
    /*
     * Guarded on `busy` alone, and on nothing else.
     *
     * This used to read `busy || loading || !db`. But /forgot is PUBLIC and
     * exists for somebody who cannot sign in, so `db` is null every single
     * time it is used — the form returned here silently and never sent a
     * thing. `loading` was the same trap from the other side: a stale session
     * restoring against a sleeping API held it true for the better part of a
     * minute, with the button doing nothing and saying nothing.
     *
     * requestPasswordReset is a bare call to a public endpoint (ClubContext:
     * `api.forgotPassword`). It needs no club and no session.
     */
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      setSent(await requestPasswordReset(identifier));
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that reset link.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell step={sent ? 2 : 1}>
      {sent ? (
        <SentCard
          sent={sent}
          onAgain={() => {
            setSent(null);
            setError('');
          }}
        />
      ) : (
        <>
          <h1 className="font-market text-[27px] font-extrabold leading-none tracking-[-0.03em]">
            Forgot password
          </h1>
          <p className="mt-2.5 text-[13px] leading-relaxed text-copy-muted">
            Tell us your mobile number or your email. We’ll send a link to the address on your
            membership so you can set a new password.
          </p>

          <form onSubmit={submit} className="mt-6">
            <div className="mb-5">
              <label
                htmlFor="f-id"
                className="mb-1.5 block font-market text-[9.5px] uppercase tracking-[0.09em] text-copy-muted"
              >
                Mobile number or email
              </label>
              <div className="relative flex items-center">
                <svg
                  className="pointer-events-none absolute left-3.5"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#94A3B8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="8" r="3.6" />
                  <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
                </svg>
                <input
                  id="f-id"
                  type="text"
                  // Email keyboard always — the number pad has no "@". See
                  // IdentifierField in SignIn.jsx.
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="username"
                  autoFocus
                  placeholder={IDENTIFIER_PLACEHOLDER}
                  className={INPUT}
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                    setError('');
                  }}
                />
              </div>
              {echo && <div className="mt-1.5 font-market text-[10.5px] text-copy-label">{echo}</div>}
            </div>

            <SubmitButton busy={busy} label="Email me a reset link" busyLabel="Sending…" />
            <Feedback error={error} notice="" />
          </form>

          <Fineprint
            items={[
              `The link works once, and expires ${RESET_TTL_MIN} minutes after it is sent.`,
              'Asking again voids the older link — only the newest email works.',
              'Give us a number and we’ll show you which inbox it went to.',
            ]}
          />
        </>
      )}
    </Shell>
  );
}

/**
 * What they see once it is away.
 *
 * The masked address is the payload — "check THAT inbox". The screen
 * deliberately does not say "if an account exists": the rest of this app
 * already tells an unknown number it is not on the roster (see
 * DENIAL_MESSAGE), and pretending otherwise here would only make a locked-out
 * member wait for an email that is never coming.
 */
function SentCard({ sent, onAgain }) {
  return (
    <>
      <div className="grid h-11 w-11 place-items-center bg-sea-50 text-sea-600">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
          <path d="m3 7 9 6 9-6" />
        </svg>
      </div>

      <h1 className="mt-4 font-market text-[27px] font-extrabold leading-tight tracking-[-0.03em]">
        Check your email
      </h1>
      <p className="mt-2 text-[13px] leading-relaxed text-copy-muted">
        A link to set a new password is on its way to:
      </p>

      <div className="mt-3.5 overflow-hidden border border-line">
        <div className="border-b border-line bg-ground px-4 py-2 text-center font-market text-[9px] uppercase tracking-[0.14em] text-copy-label">
          Sent to
        </div>
        <div className="break-all bg-white px-4 py-3.5 text-center font-market text-[15px] font-semibold text-ink-700">
          {sent.maskedEmail}
        </div>
      </div>

      <p className="mt-4 text-[12.5px] leading-relaxed text-copy-muted">
        Open that inbox and tap <b>Set a new password</b>. If it is not there in a couple of
        minutes, check your spam folder.
      </p>

      <button
        type="button"
        onClick={onAgain}
        className="mt-5 w-full border border-line py-3 font-market text-[14px] font-bold tracking-[-0.015em] text-copy-muted transition-all duration-200 hover:border-gold-500 hover:text-gold-800"
      >
        Use a different number or email
      </button>
      <p className="mt-2 text-center font-market text-[9.5px] leading-relaxed text-copy-label">
        Sending again voids this link — only the newest email will work.
      </p>
    </>
  );
}

/** The rules of the link, as a list rather than a paragraph nobody finishes. */
export function Fineprint({ items }) {
  return (
    <ul className="mt-4 space-y-2 border-t border-line pt-4">
      {items.map((item) => (
        <li key={item} className="flex gap-2.5 text-[11.5px] leading-relaxed text-copy-label">
          <span
            className="mt-[7px] h-1 w-1 flex-none rounded-full bg-gold-500"
            aria-hidden="true"
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The narrow card both password screens sit in.
 *
 * `step` draws the two-step progress: ask, then open the email. It is the one
 * piece of chrome that earns its space here — a member who knows an email is
 * coming waits for it instead of retrying the form.
 */
export function Shell({ children, step }) {
  return (
    <div className="min-h-screen bg-ground">
      <header className="bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_52%,#0A0E19_100%)] px-5 py-4">
        <div className="mx-auto flex max-w-[440px] items-center justify-between gap-4">
          <Brand dark />
          <Link
            to="/signin"
            className="font-market text-[9.5px] uppercase tracking-[0.11em] text-copy-label transition-colors hover:text-gold-500"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[440px] px-5 py-8">
        {step ? <Steps current={step} /> : null}

        <div className="border border-line bg-white p-6 shadow-sm sm:p-7">
          {children}
        </div>

        <div className="mt-6 text-center text-[12.5px] text-copy-muted">
          <Link
            to="/signin"
            className="font-semibold text-gold-800 underline underline-offset-2 hover:text-gold-900"
          >
            ‹ Back to sign in
          </Link>
        </div>
      </main>
    </div>
  );
}

const STEPS = ['Your details', 'Open the email', 'New password'];

/** Where in the errand they are. Amber = here, ink = done, grey = ahead. */
function Steps({ current }) {
  return (
    <ol className="mb-5 flex items-center gap-2">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const here = n === current;
        return (
          <li key={label} className="flex flex-1 flex-col gap-1.5">
            <span
              className={`h-1 rounded-full transition-colors duration-300 ${
                here ? 'bg-gold-500' : done ? 'bg-ink-800' : 'bg-line'
              }`}
            />
            <span
              className={`font-market text-[8.5px] uppercase tracking-[0.11em] ${
                here ? 'text-ink-700' : 'text-copy-label'
              }`}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
